import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, LogOut, Phone, CheckCircle, XCircle, MapPin, AlertTriangle, Search, Filter, X, MessageSquare, Image as ImageIcon, Clock, Eye, EyeOff, UserPlus, Users, Edit2, Lock, Calendar, User, FileText, Wifi, Bell, BellOff, BarChart3 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import TicketCard from '../components/TicketCard';
import ConnectionsPanel from '../components/ConnectionsPanel';
import D2DPanel from '../components/D2DPanel';
import TechnicianPerformancePanel from '../components/dashboard/TechnicianPerformancePanel';
import MapPanel from '../components/dashboard/MapPanel';
import HRPanel from '../components/dashboard/HRPanel';
import AccountingPanel from '../components/dashboard/AccountingPanel';
import TasksPanel from '../components/tasks/TasksPanel';
import { ImageViewer } from '../components/ui/image-viewer';
import NotificationBell from '../components/dashboard/NotificationBell';
import TopBarTasksButton from '../components/tasks/TopBarTasksButton';
// Refactored components - available for future use
// import { TicketFilters, TicketStats, TicketTabs, AssignTicketDialog } from '../components/operator';
// import { getMinDate, CATEGORIES } from '../components/shared/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to extract image URL from different formats
const getImageUrl = (img) => {
  if (!img) return '';
  if (typeof img === 'string') return img;
  if (typeof img === 'object') {
    return img.thumbnail || img.full || '';
  }
  return '';
};

// Helper function to open base64 image in new tab
const openImageInNewTab = async (img, ticketId = null, imgIndex = 0) => {
  let imageUrl = getImageUrl(img);
  if (!imageUrl) return;
  
  // Try to get full resolution image from API if ticketId is provided
  if (ticketId) {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/tickets/${ticketId}/full-images`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.images && response.data.images.length > imgIndex) {
        imageUrl = response.data.images[imgIndex];
      }
    } catch (error) {
      console.error('Error fetching full image:', error);
    }
  }
  
  // If it's a base64 image, convert to blob and open
  if (imageUrl.startsWith('data:')) {
    try {
      const byteString = atob(imageUrl.split(',')[1]);
      const mimeType = imageUrl.split(',')[0].split(':')[1].split(';')[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: mimeType });
      const blobUrl = URL.createObjectURL(blob);
      window.open(blobUrl, '_blank');
    } catch (error) {
      console.error('Error opening image:', error);
      const newWindow = window.open('', '_blank');
      if (newWindow) {
        newWindow.document.write(`<html><head><title>Şəkil</title></head><body style="margin:0;display:flex;justify-content:center;align-items:center;min-height:100vh;background:#000;"><img src="${imageUrl}" style="max-width:100%;max-height:100vh;object-fit:contain;"/></body></html>`);
        newWindow.document.close();
      }
    }
  } else {
    window.open(imageUrl, '_blank');
  }
};

export default function OperatorDashboard({ user, onLogout }) {
  const [tickets, setTickets] = useState([]);
  const [technicians, setTechnicians] = useState([]);
  const [groups, setGroups] = useState([]);
  const [problemTypes, setProblemTypes] = useState({});
  const [loading, setLoading] = useState(true);
  // Main tab state (tickets or connections)
  const [mainTab, setMainTab] = useState('tickets');

  // Module access check - empty modules means full access
  const hasModule = (moduleId) => {
    if (!user.modules || user.modules.length === 0) return true;
    return user.modules.includes(moduleId);
  };
  // Search/Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  // Tab state
  const [activeTab, setActiveTab] = useState('pending');
  const [showClosedTickets, setShowClosedTickets] = useState(false);
  // Comment/Image dialog state
  const [showCommentImageDialog, setShowCommentImageDialog] = useState(false);
  const [selectedTicketForDetails, setSelectedTicketForDetails] = useState(null);
  // Full screen image viewer state
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [imageViewerImages, setImageViewerImages] = useState([]);
  const [imageViewerIndex, setImageViewerIndex] = useState(0);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectTicket, setRejectTicket] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  // Assign technician dialog state
  const [showAssignDialog, setShowAssignDialog] = useState(false);
  const [assignTicket, setAssignTicket] = useState(null);
  const [assignData, setAssignData] = useState({
    technician_id: '',
    scheduled_date: '',
    scheduled_time: ''
  });
  // Close ticket dialog state
  const [showCloseDialog, setShowCloseDialog] = useState(false);
  const [closeTicket, setCloseTicket] = useState(null);
  const [customerSatisfaction, setCustomerSatisfaction] = useState(null);
  
  // Detailed view and reschedule dialog state
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [detailTicket, setDetailTicket] = useState(null);
  const [canReschedule, setCanReschedule] = useState(false);
  const [rescheduleReason, setRescheduleReason] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [rescheduleData, setRescheduleData] = useState({
    technician_id: '',
    scheduled_date: '',
    scheduled_time: '',
    operator_notes: ''
  });
  const [groupTechnicians, setGroupTechnicians] = useState([]);
  
  // Safe dialog close handlers for OperatorDashboard
  const safeCloseRejectDialog = (open) => {
    if (!open) {
      setShowRejectDialog(false);
      setRejectionReason('');
      setTimeout(() => setRejectTicket(null), 100);
    }
  };
  
  const safeCloseAssignDialog = (open) => {
    if (!open) {
      setShowAssignDialog(false);
      setAssignData({ technician_id: '', scheduled_date: '', scheduled_time: '' });
      setTimeout(() => setAssignTicket(null), 100);
    }
  };
  
  const safeCloseCloseDialog = (open) => {
    if (!open) {
      setShowCloseDialog(false);
      setCustomerSatisfaction(null);
      setTimeout(() => setCloseTicket(null), 100);
    }
  };
  
  const safeCloseCommentDialog = (open) => {
    if (!open) {
      setShowCommentImageDialog(false);
      setTimeout(() => setSelectedTicketForDetails(null), 100);
    }
  };
  
  const safeCloseDetailDialog = (open) => {
    if (!open) {
      setShowDetailDialog(false);
      setIsEditMode(false);
      setRescheduleData({ technician_id: '', scheduled_date: '', scheduled_time: '', operator_notes: '' });
      setTimeout(() => {
        setDetailTicket(null);
        setCanReschedule(false);
        setRescheduleReason('');
        setGroupTechnicians([]);
      }, 100);
    }
  };
  
  // ABillS integration
  const [abillsUser, setAbillsUser] = useState('');
  const [abillsLoading, setAbillsLoading] = useState(false);
  const [abillsData, setAbillsData] = useState(null);
  const [newTicket, setNewTicket] = useState({
    group_id: '',
    category: 'internet',
    subscriber_name: '',
    subscriber_phone: '',
    subscriber_phone_2: '',
    subscriber_location: '',
    latitude: null,
    longitude: null,
    contract_number: '',
    service_password: '',
    city_phone: '',
    city_phone_password: '',
    problem_type: '',
    problem_description: '',
    operator_notes: '',
    auto_assign: false,
    onu_rx_power: null,
    onu_tx_power: null,
    olt_rx_power: null
  });

  const token = localStorage.getItem('token');

  // ABillS-dən istifadəçi məlumatlarını al
  const fetchAbillsUser = async () => {
    if (!abillsUser.trim()) {
      toast.error('User daxil edin');
      return;
    }
    
    setAbillsLoading(true);
    try {
      const response = await axios.get(`${API}/abills/user/${abillsUser.trim()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setAbillsData(response.data);
        // Form-u doldur
        setNewTicket(prev => ({
          ...prev,
          subscriber_name: response.data.full_name || prev.subscriber_name,
          subscriber_phone: response.data.phone || prev.subscriber_phone,
          contract_number: response.data.contract_number || prev.contract_number,
          service_password: response.data.password || prev.service_password,
          subscriber_location: response.data.address || prev.subscriber_location,
          onu_rx_power: response.data.onu_rx_power,
          onu_tx_power: response.data.onu_tx_power
        }));
        toast.success('Məlumatlar ABillS-dən alındı');
      } else {
        toast.error(response.data.error || 'İstifadəçi tapılmadı');
        setAbillsData(null);
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'ABillS-ə qoşulmaq mümkün olmadı';
      toast.error(errorMsg);
      setAbillsData(null);
    } finally {
      setAbillsLoading(false);
    }
  };

  const fetchProblemTypes = async () => {
    try {
      const response = await axios.get(`${API}/problem-types/by-category`);
      setProblemTypes(response.data);
    } catch (error) {
      console.error('Problem növlərini yükləmək mümkün olmadı');
    }
  };

  const fetchTickets = async () => {
    try {
      const response = await axios.get(`${API}/tickets`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTickets(response.data);
    } catch (error) {
      toast.error('Ticketləri yükləmək mümkün olmadı');
    }
  };

  const fetchTechnicians = async () => {
    try {
      const response = await axios.get(`${API}/technicians`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTechnicians(response.data);
    } catch (error) {
      toast.error('Texnikləri yükləmək mümkün olmadı');
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await axios.get(`${API}/groups`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGroups(response.data);
    } catch (error) {
      console.error('Groups fetch error:', error);
    }
  };

  // Fetch technicians for a specific group
  const fetchGroupTechnicians = async (groupId) => {
    try {
      const response = await axios.get(`${API}/groups/${groupId}/technicians`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('Group technicians fetch error:', error);
      return [];
    }
  };

  // Notification state (removed FCM — using in-app polling instead)

  useEffect(() => {
    Promise.all([fetchTickets(), fetchTechnicians(), fetchGroups(), fetchProblemTypes()]).finally(() => setLoading(false));
    const interval = setInterval(fetchTickets, 30000);
    return () => clearInterval(interval);
  }, []);

  // Handle clicks from notification bell — switch tab and let panels handle entity opening via event
  const handleCommentNotificationClick = ({ entity_type, entity_id }) => {
    if (entity_type === 'ticket') {
      setMainTab('tickets');
      // find ticket in current state and open its detail dialog
      const found = (tickets || []).find((t) => t.id === entity_id);
      if (found) {
        setDetailTicket(found);
        setShowDetailDialog(true);
      } else {
        // fallback: refetch and retry
        fetchTickets().then(() => {
          // Use a brief timeout to wait for state update
          setTimeout(() => {
            // re-find after refetch — caller should have new state via closure
            window.dispatchEvent(new CustomEvent('open-ticket', { detail: { id: entity_id } }));
          }, 200);
        });
      }
    } else if (entity_type === 'connection') {
      setMainTab('connections');
      window.dispatchEvent(new CustomEvent('open-connection', { detail: { id: entity_id } }));
    }
  };

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!newTicket.group_id) {
      toast.error('Qrup seçin');
      return;
    }
    try {
      const response = await axios.post(`${API}/tickets`, newTicket, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Avtomatik təyin edildisə fərqli mesaj göstər
      if (newTicket.auto_assign && response.data.assigned_technician_id) {
        toast.success(`Ticket yaradıldı və ${response.data.assigned_technician_name}-a avtomatik təyin edildi`);
      } else if (newTicket.auto_assign && !response.data.assigned_technician_id) {
        toast.warning('Ticket yaradıldı, amma bu qrupda texnik tapılmadı');
      } else {
        toast.success('Ticket yaradıldı');
      }
      
      setShowCreateDialog(false);
      setNewTicket({
        group_id: '',
        category: 'internet',
        subscriber_name: '',
        subscriber_phone: '',
        subscriber_location: '',
        latitude: null,
        longitude: null,
        contract_number: '',
        service_password: '',
        city_phone: '',
        city_phone_password: '',
        problem_type: '',
        problem_description: '',
        operator_notes: '',
        auto_assign: false,
        onu_rx_power: null,
        onu_tx_power: null,
        olt_rx_power: null
      });
      fetchTickets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Ticket yaratmaq mümkün olmadı');
    }
  };

  // Get technicians filtered by ticket's group
  const getTechniciansForTicket = (ticket) => {
    if (!ticket.group_id) return technicians;
    return technicians.filter(t => t.groups?.includes(ticket.group_id));
  };

  // Open detail dialog and check if can reschedule
  const openDetailDialog = async (ticket) => {
    setDetailTicket(ticket);
    setShowDetailDialog(true);
    setIsEditMode(false);
    setRescheduleData({
      technician_id: ticket.assigned_technician_id || '',
      scheduled_date: ticket.scheduled_date || '',
      scheduled_time: ticket.scheduled_time || '',
      operator_notes: ''
    });
    
    // Get technicians for this ticket's group
    const techs = getTechniciansForTicket(ticket);
    setGroupTechnicians(techs);
    
    // Fetch full ticket details including images
    try {
      const token = localStorage.getItem('token');
      const detailRes = await axios.get(
        `${API}/tickets/${ticket.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setDetailTicket(detailRes.data);
    } catch (error) {
      console.error('Error fetching ticket details:', error);
    }
    
    // Check if can reschedule
    try {
      const res = await axios.get(
        `${API}/tickets/${ticket.id}/can-reschedule`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCanReschedule(res.data.can_reschedule);
      setRescheduleReason(res.data.reason || '');
    } catch (error) {
      setCanReschedule(false);
      setRescheduleReason('Yoxlama zamanı xəta baş verdi');
    }
  };

  // Handle reschedule
  const handleReschedule = async () => {
    if (!detailTicket) return;
    
    try {
      const payload = {};
      
      // Only include changed values
      if (rescheduleData.technician_id && rescheduleData.technician_id !== detailTicket.assigned_technician_id) {
        payload.technician_id = rescheduleData.technician_id;
      }
      if (rescheduleData.scheduled_date && rescheduleData.scheduled_date !== detailTicket.scheduled_date) {
        payload.scheduled_date = rescheduleData.scheduled_date;
      }
      if (rescheduleData.scheduled_time && rescheduleData.scheduled_time !== detailTicket.scheduled_time) {
        payload.scheduled_time = rescheduleData.scheduled_time;
      }
      if (rescheduleData.operator_notes && rescheduleData.operator_notes.trim()) {
        payload.operator_notes = rescheduleData.operator_notes.trim();
      }
      
      if (Object.keys(payload).length === 0) {
        toast.error('Heç bir dəyişiklik edilmədi');
        return;
      }
      
      await axios.patch(
        `${API}/tickets/${detailTicket.id}/reschedule`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Servis uğurla yeniləndi');
      setShowDetailDialog(false);
      setIsEditMode(false);
      setDetailTicket(null);
      setRescheduleData({ technician_id: '', scheduled_date: '', scheduled_time: '', operator_notes: '' });
      fetchTickets();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Yeniləmə zamanı xəta baş verdi';
      toast.error(errorMsg);
    }
  };

  const handleAssignTechnician = async (ticketId, technicianId, scheduledDate, scheduledTime) => {
    try {
      const updateData = { 
        assigned_technician_id: technicianId, 
        status: 'assigned'
      };
      
      if (scheduledDate) {
        updateData.scheduled_date = scheduledDate;
      }
      if (scheduledTime) {
        updateData.scheduled_time = scheduledTime;
      }
      
      await axios.patch(
        `${API}/tickets/${ticketId}`,
        updateData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Texnikə təyin edildi');
      setShowAssignDialog(false);
      setAssignTicket(null);
      setAssignData({ technician_id: '', scheduled_date: '', scheduled_time: '' });
      fetchTickets();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Təyin etmək mümkün olmadı';
      toast.error(errorMsg);
    }
  };

  const openAssignDialog = (ticket) => {
    setAssignTicket(ticket);
    setAssignData({ technician_id: '', scheduled_date: '', scheduled_time: '' });
    setShowAssignDialog(true);
  };

  const handleCloseTicket = async (ticketId, satisfaction) => {
    try {
      await axios.patch(
        `${API}/tickets/${ticketId}`,
        { status: 'closed', customer_satisfaction: satisfaction },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ticket bağlandı');
      setShowCloseDialog(false);
      setCloseTicket(null);
      setCustomerSatisfaction(null);
      fetchTickets();
    } catch (error) {
      toast.error('Bağlamaq mümkün olmadı');
    }
  };

  const openCloseDialog = (ticket) => {
    setCloseTicket(ticket);
    setCustomerSatisfaction(null);
    setShowCloseDialog(true);
  };

  const handleShowRejectDialog = (ticket) => {
    setRejectTicket(ticket);
    setRejectionReason('');
    setShowRejectDialog(true);
  };

  const handleRejectTicket = async () => {
    if (!rejectTicket) return;
    
    try {
      await axios.patch(
        `${API}/tickets/${rejectTicket.id}`,
        { status: 'rejected', rejection_reason: rejectionReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Ticket geri qaytarıldı');
      fetchTickets();
      setShowRejectDialog(false);
      setRejectTicket(null);
      setRejectionReason('');
    } catch (error) {
      toast.error('Geri qaytarmaq mümkün olmadı');
    }
  };

  // Filter tickets based on search/filter criteria
  const filteredTickets = useMemo(() => {
    return tickets.filter(ticket => {
      // Search term filter (name, phone, location, contract number, ticket ID)
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch = !searchTerm || 
        ticket.subscriber_name?.toLowerCase().includes(searchLower) ||
        ticket.subscriber_phone?.includes(searchTerm) ||
        ticket.subscriber_location?.toLowerCase().includes(searchLower) ||
        ticket.contract_number?.toLowerCase().includes(searchLower) ||
        ticket.id?.toLowerCase().includes(searchLower);
      
      // Category filter
      const matchesCategory = !filterCategory || filterCategory === 'all' || ticket.category === filterCategory;
      
      // Group filter
      const matchesGroup = !filterGroup || filterGroup === 'all' || ticket.group_id === filterGroup;
      
      // Status filter
      const matchesStatus = !filterStatus || filterStatus === 'all' || ticket.status === filterStatus;
      
      return matchesSearch && matchesCategory && matchesGroup && matchesStatus;
    });
  }, [tickets, searchTerm, filterCategory, filterGroup, filterStatus]);

  const clearFilters = () => {
    setSearchTerm('');
    setFilterCategory('');
    setFilterGroup('');
    setFilterStatus('');
  };

  const hasActiveFilters = searchTerm || (filterCategory && filterCategory !== 'all') || (filterGroup && filterGroup !== 'all') || (filterStatus && filterStatus !== 'all');

  // Tab-based ticket counts
  const pendingTickets = filteredTickets.filter(t => t.status === 'pending');
  const assignedTickets = filteredTickets.filter(t => ['assigned', 'in_progress'].includes(t.status));
  const completedTickets = filteredTickets.filter(t => t.status === 'completed');
  const closedTickets = filteredTickets.filter(t => t.status === 'closed');

  // Get tickets for current tab
  const getTabTickets = () => {
    switch (activeTab) {
      case 'pending':
        return pendingTickets;
      case 'assigned':
        return assignedTickets;
      case 'completed':
        return completedTickets;
      case 'closed':
        return showClosedTickets ? closedTickets : [];
      case 'all':
        return showClosedTickets ? filteredTickets : filteredTickets.filter(t => t.status !== 'closed');
      default:
        return pendingTickets;
    }
  };

  const currentTabTickets = getTabTickets();

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen">Yüklənir...</div>;
  }

  return (
    <div className="min-h-screen bg-zinc-50">
      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold uppercase tracking-tight text-zinc-900" data-testid="operator-dashboard-title">
                Operator Paneli
              </h1>
              <p className="text-sm text-zinc-600 mt-1">Xoş gəldiniz, {user.full_name || user.username}</p>
            </div>
            <div className="flex items-center gap-2">
              <TopBarTasksButton onOpenTasksPage={() => setMainTab('tasks')} />
              {/* In-app notifications: comments + broadcast */}
              <NotificationBell type="comment" onCommentClick={handleCommentNotificationClick} />
              <NotificationBell type="broadcast" />

              <Button
                onClick={onLogout}
                variant="outline"
                className="flex items-center gap-2"
                data-testid="logout-button"
              >
                <LogOut className="h-4 w-4" />
                Çıxış
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Tab Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <div className="flex flex-wrap gap-2 mb-4 overflow-x-auto pb-2">
          {hasModule('services') && (
          <Button
            onClick={() => setMainTab('tickets')}
            variant={mainTab === 'tickets' ? 'default' : 'outline'}
            className={`whitespace-nowrap ${mainTab === 'tickets' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
          >
            <AlertTriangle className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Servislər</span>
            <span className="sm:hidden">Servis</span>
          </Button>
          )}
          {hasModule('connections') && (
          <Button
            onClick={() => setMainTab('connections')}
            variant={mainTab === 'connections' ? 'default' : 'outline'}
            className={`whitespace-nowrap ${mainTab === 'connections' ? 'bg-green-600 hover:bg-green-700' : ''}`}
          >
            <UserPlus className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Qoşulmalar</span>
            <span className="sm:hidden">Qoşulma</span>
          </Button>
          )}
          {hasModule('d2d') && (
          <Button
            onClick={() => setMainTab('d2d')}
            variant={mainTab === 'd2d' ? 'default' : 'outline'}
            className={`whitespace-nowrap ${mainTab === 'd2d' ? 'bg-orange-600 hover:bg-orange-700' : ''}`}
            data-testid="main-tab-d2d"
          >
            <Users className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">D2D Qeydləri</span>
            <span className="sm:hidden">D2D</span>
          </Button>
          )}
          {hasModule('performance') && (
          <Button
            onClick={() => setMainTab('performance')}
            variant={mainTab === 'performance' ? 'default' : 'outline'}
            className={`whitespace-nowrap ${mainTab === 'performance' ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
            data-testid="main-tab-performance"
          >
            <BarChart3 className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Texnik Performans</span>
            <span className="sm:hidden">Performans</span>
          </Button>
          )}
          {hasModule('map') && (
          <Button
            onClick={() => setMainTab('map')}
            variant={mainTab === 'map' ? 'default' : 'outline'}
            className={`whitespace-nowrap ${mainTab === 'map' ? 'bg-teal-600 hover:bg-teal-700' : ''}`}
            data-testid="main-tab-map"
          >
            <MapPin className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Xəritə</span>
            <span className="sm:hidden">Xəritə</span>
          </Button>
          )}
          {hasModule('hr') && (
          <Button
            onClick={() => setMainTab('hr')}
            variant={mainTab === 'hr' ? 'default' : 'outline'}
            className={`whitespace-nowrap ${mainTab === 'hr' ? 'bg-cyan-600 hover:bg-cyan-700' : ''}`}
            data-testid="main-tab-hr"
          >
            <Clock className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">HR</span>
            <span className="sm:hidden">HR</span>
          </Button>
          )}
          {hasModule('accounting') && (
          <Button
            onClick={() => setMainTab('accounting')}
            variant={mainTab === 'accounting' ? 'default' : 'outline'}
            className={`whitespace-nowrap ${mainTab === 'accounting' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
            data-testid="main-tab-accounting"
          >
            <span className="hidden sm:inline">💰 Mühasibatlıq</span>
            <span className="sm:hidden">💰</span>
          </Button>
          )}
          <Button
            onClick={() => setMainTab('tasks')}
            variant={mainTab === 'tasks' ? 'default' : 'outline'}
            className={`whitespace-nowrap ${mainTab === 'tasks' ? 'bg-purple-600 hover:bg-purple-700' : ''}`}
            data-testid="main-tab-tasks"
          >
            <span className="hidden sm:inline">Tapşırıqlar</span>
            <span className="sm:hidden">📋</span>
          </Button>
        </div>
      </div>

      {/* D2D Panel - Always rendered but hidden for performance */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6" style={{ display: mainTab === 'd2d' ? 'block' : 'none' }}>
        <D2DPanel user={user} />
      </div>

      {/* Connections Panel - Always rendered but hidden for performance */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6" style={{ display: mainTab === 'connections' ? 'block' : 'none' }}>
        <ConnectionsPanel user={user} />
      </div>

      {/* Texnik Performans Panel */}
      {mainTab === 'performance' && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
          <TechnicianPerformancePanel user={user} />
        </div>
      )}

      {/* Xəritə Panel */}
      {mainTab === 'map' && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
          <MapPanel user={user} />
        </div>
      )}

      {/* HR Panel */}
      {mainTab === 'hr' && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
          <HRPanel user={user} />
        </div>
      )}

      {/* Accounting Panel */}
      {mainTab === 'accounting' && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
          <AccountingPanel user={user} />
        </div>
      )}

      {/* Tasks Panel (Standalone Tasks - Sprint 10-11) */}
      {mainTab === 'tasks' && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-6">
          <TasksPanel user={user} />
        </div>
      )}

      {/* Tickets Panel */}
      {mainTab === 'tickets' && (
      <>
      {/* Stats */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 bg-white p-2 rounded-lg border border-zinc-200">
          <Button
            onClick={() => setActiveTab('pending')}
            variant={activeTab === 'pending' ? 'default' : 'ghost'}
            className={activeTab === 'pending' ? 'bg-amber-600 hover:bg-amber-700' : ''}
            data-testid="tab-pending"
          >
            <Clock className="h-4 w-4 mr-2" />
            Gözləyən ({pendingTickets.length})
          </Button>
          <Button
            onClick={() => setActiveTab('assigned')}
            variant={activeTab === 'assigned' ? 'default' : 'ghost'}
            className={activeTab === 'assigned' ? 'bg-blue-600 hover:bg-blue-700' : ''}
            data-testid="tab-assigned"
          >
            Təyin olunmuş ({assignedTickets.length})
          </Button>
          <Button
            onClick={() => setActiveTab('completed')}
            variant={activeTab === 'completed' ? 'default' : 'ghost'}
            className={activeTab === 'completed' ? 'bg-green-600 hover:bg-green-700' : ''}
            data-testid="tab-completed"
          >
            <CheckCircle className="h-4 w-4 mr-2" />
            Tamamlanmış ({completedTickets.length})
          </Button>
          <Button
            onClick={() => setActiveTab('closed')}
            variant={activeTab === 'closed' ? 'default' : 'ghost'}
            className={activeTab === 'closed' ? 'bg-zinc-600 hover:bg-zinc-700' : ''}
            data-testid="tab-closed"
          >
            Bağlanmış ({closedTickets.length})
          </Button>
          <Button
            onClick={() => setActiveTab('all')}
            variant={activeTab === 'all' ? 'default' : 'ghost'}
            className={activeTab === 'all' ? 'bg-purple-600 hover:bg-purple-700' : ''}
            data-testid="tab-all"
          >
            Bütün Tiketlər ({filteredTickets.length})
          </Button>
          
          {/* Show/Hide Closed Toggle */}
          <div className="ml-auto flex items-center">
            <Button
              onClick={() => setShowClosedTickets(!showClosedTickets)}
              variant="outline"
              size="sm"
              className={showClosedTickets ? 'text-green-600 border-green-300' : 'text-zinc-500'}
              data-testid="toggle-closed-tickets"
            >
              {showClosedTickets ? <Eye className="h-4 w-4 mr-2" /> : <EyeOff className="h-4 w-4 mr-2" />}
              {showClosedTickets ? 'Bağlanmış görünür' : 'Bağlanmış gizli'}
            </Button>
          </div>
        </div>

        {/* Search and Filter Section */}
        <div className="bg-white p-4 rounded-lg border border-gray-400 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="h-5 w-5 text-zinc-600" />
            <h2 className="font-semibold text-zinc-900">Axtarış və Filtrlər</h2>
            {hasActiveFilters && (
              <Button variant="ghost" size="sm" onClick={clearFilters} className="ml-auto text-red-600 hover:text-red-700">
                <X className="h-4 w-4 mr-1" />
                Filtrləri Təmizlə
              </Button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
              <Input
                placeholder="Axtarış et..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 border-gray-400 focus:border-gray-500 placeholder:text-gray-500"
                data-testid="search-input"
              />
            </div>
            <Select value={filterGroup} onValueChange={setFilterGroup}>
              <SelectTrigger data-testid="filter-group" className="border-gray-400 focus:border-gray-500 text-gray-500 data-[state=open]:text-gray-900 [&>span:first-child]:text-gray-500">
                <SelectValue placeholder="Qrup" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Bütün Qruplar</SelectItem>
                {groups.map((group) => (
                  <SelectItem key={group.id} value={group.id}>{group.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger data-testid="filter-category" className="border-gray-400 focus:border-gray-500 text-gray-500 data-[state=open]:text-gray-900 [&>span:first-child]:text-gray-500">
                <SelectValue placeholder="Servis Kateqoriyası" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Bütün Kateqoriyalar</SelectItem>
                <SelectItem value="internet">İnternet</SelectItem>
                <SelectItem value="iptv">IPTV</SelectItem>
                <SelectItem value="catv">CATV</SelectItem>
                <SelectItem value="telefon">Telefon</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger data-testid="filter-status" className="border-gray-400 focus:border-gray-500 text-gray-500 data-[state=open]:text-gray-900 [&>span:first-child]:text-gray-500">
                <SelectValue placeholder="Servisin Statusu" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Bütün Statuslar</SelectItem>
                <SelectItem value="pending">Gözləyən</SelectItem>
                <SelectItem value="assigned">Təyin olunmuş</SelectItem>
                <SelectItem value="completed">Tamamlanmış</SelectItem>
                <SelectItem value="closed">Bağlanmış</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {hasActiveFilters && (
            <p className="text-sm text-zinc-500 mt-2">
              {filteredTickets.length} ticket tapıldı (cəmi {tickets.length})
            </p>
          )}
        </div>

        {/* Create Ticket Button */}
        <Dialog open={showCreateDialog} onOpenChange={(open) => {
          setShowCreateDialog(open);
          if (!open) {
            setAbillsUser('');
            setAbillsData(null);
          }
        }}>
          <DialogTrigger asChild>
            <Button className="mb-6 bg-primary hover:bg-primary/90 text-white" data-testid="create-ticket-button">
              <Plus className="h-4 w-4 mr-2" />
              Yeni Ticket Yarat
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="create-ticket-dialog">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold">Yeni Ticket</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateTicket} className="space-y-4">
              {/* ABillS User Search */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                <Label className="text-blue-800 font-semibold">Müştəri məlumatlarını avtomatik doldur</Label>
                <div className="flex gap-2">
                  <Input
                    placeholder="User ID / Login daxil edin"
                    value={abillsUser}
                    onChange={(e) => setAbillsUser(e.target.value)}
                    className="flex-1 placeholder:text-gray-500"
                    data-testid="abills-user-input"
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); fetchAbillsUser(); } }}
                  />
                  <Button 
                    type="button" 
                    onClick={fetchAbillsUser}
                    disabled={abillsLoading}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="abills-fetch-btn"
                  >
                    {abillsLoading ? 'Yüklənir...' : 'Məlumatları Doldur'}
                  </Button>
                </div>
                {abillsData && abillsData.success && (
                  <div className="text-sm text-blue-700 space-y-1">
                    {abillsData.onu_rx_power !== null && (
                      <p>📡 ONU RX: <span className={abillsData.onu_rx_power < -28 ? 'text-red-600 font-bold' : 'text-green-600'}>{abillsData.onu_rx_power} dBm</span>
                      {abillsData.onu_tx_power !== null && <> | TX: {abillsData.onu_tx_power} dBm</>}</p>
                    )}
                    {abillsData.tariff && <p>📋 Tarif: {abillsData.tariff}</p>}
                    {abillsData.balance !== null && <p>💰 Balans: {abillsData.balance} AZN</p>}
                  </div>
                )}
              </div>

              {/* Group Selection */}
              <div>
                <Label htmlFor="group">Qrup / Ərazi *</Label>
                <Select 
                  value={newTicket.group_id} 
                  onValueChange={(value) => setNewTicket({ ...newTicket, group_id: value })}
                  required
                >
                  <SelectTrigger className="mt-1.5 border-gray-300" data-testid="group-select">
                    <SelectValue placeholder="Qrup seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    {groups.map((group) => (
                      <SelectItem key={group.id} value={group.id}>{group.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Category Selection */}
              <div>
                <Label htmlFor="category">Kateqoriya</Label>
                <Select 
                  value={newTicket.category} 
                  onValueChange={(value) => setNewTicket({ ...newTicket, category: value, problem_type: '', problem_description: '' })}
                >
                  <SelectTrigger className="mt-1.5 border-gray-300" data-testid="category-select">
                    <SelectValue placeholder="Kateqoriya seçin" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="internet">İnternet</SelectItem>
                    <SelectItem value="iptv">IPTV</SelectItem>
                    <SelectItem value="catv">CATV</SelectItem>
                    <SelectItem value="telefon">Telefon</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="subscriber_name">Abunəçi Soyadı, Adı, Ata adı</Label>
                <Input
                  id="subscriber_name"
                  value={newTicket.subscriber_name}
                  onChange={(e) => setNewTicket({ ...newTicket, subscriber_name: e.target.value })}
                  required
                  data-testid="subscriber-name-input"
                  className="mt-1.5 border-gray-300"
                />
              </div>
              
              {/* Phone numbers side by side */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="subscriber_phone">Telefon</Label>
                  <Input
                    id="subscriber_phone"
                    value={newTicket.subscriber_phone}
                    onChange={(e) => setNewTicket({ ...newTicket, subscriber_phone: e.target.value })}
                    required
                    data-testid="subscriber-phone-input"
                    className="mt-1.5 border-gray-300"
                  />
                </div>
                <div>
                  <Label htmlFor="subscriber_phone_2">Əlavə Telefon</Label>
                  <Input
                    id="subscriber_phone_2"
                    value={newTicket.subscriber_phone_2}
                    onChange={(e) => setNewTicket({ ...newTicket, subscriber_phone_2: e.target.value })}
                    data-testid="subscriber-phone-2-input"
                    className="mt-1.5 border-gray-300"
                    placeholder="(İstəyə bağlı)"
                  />
                </div>
              </div>
              
              {/* Category-specific fields */}
              {(newTicket.category === 'internet' || newTicket.category === 'iptv') && (
                <>
                  <div>
                    <Label htmlFor="contract_number">İstifadəçi</Label>
                    <Input
                      id="contract_number"
                      value={newTicket.contract_number}
                      onChange={(e) => setNewTicket({ ...newTicket, contract_number: e.target.value })}
                      data-testid="contract-number-input"
                      className="mt-1.5 border-gray-300"
                    />
                  </div>
                  <div>
                    <Label htmlFor="service_password">Şifrə</Label>
                    <Input
                      id="service_password"
                      type="text"
                      value={newTicket.service_password}
                      onChange={(e) => setNewTicket({ ...newTicket, service_password: e.target.value })}
                      data-testid="service-password-input"
                      className="mt-1.5 border-gray-300"
                    />
                  </div>
                  {/* ONU RX/TX Power */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="onu_rx_power">ONU RX Power (dBm)</Label>
                      <Input
                        id="onu_rx_power"
                        value={newTicket.onu_rx_power || ''}
                        onChange={(e) => setNewTicket({ ...newTicket, onu_rx_power: e.target.value })}
                        placeholder="—"
                        className={`mt-1.5 border-gray-300 ${newTicket.onu_rx_power && parseFloat(newTicket.onu_rx_power) < -28 ? 'border-red-500 bg-red-50' : ''}`}
                        data-testid="onu-rx-power-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="onu_tx_power">ONU TX Power (dBm)</Label>
                      <Input
                        id="onu_tx_power"
                        value={newTicket.onu_tx_power || ''}
                        onChange={(e) => setNewTicket({ ...newTicket, onu_tx_power: e.target.value })}
                        placeholder="—"
                        className="mt-1.5 border-gray-300"
                        data-testid="onu-tx-power-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="olt_rx_power">OLT RX Power (dBm)</Label>
                      <Input
                        id="olt_rx_power"
                        value={newTicket.olt_rx_power || ''}
                        onChange={(e) => setNewTicket({ ...newTicket, olt_rx_power: e.target.value })}
                        placeholder="—"
                        className={`mt-1.5 border-gray-300 ${newTicket.olt_rx_power && parseFloat(newTicket.olt_rx_power) < -28 ? 'border-red-500 bg-red-50' : ''}`}
                        data-testid="olt-rx-power-input"
                      />
                    </div>
                  </div>
                </>
              )}
              
              {newTicket.category === 'catv' && (
                <div>
                  <Label htmlFor="contract_number">İstifadəçi</Label>
                  <Input
                    id="contract_number"
                    value={newTicket.contract_number}
                    onChange={(e) => setNewTicket({ ...newTicket, contract_number: e.target.value })}
                    data-testid="contract-number-input"
                    className="mt-1.5 border-gray-300"
                  />
                </div>
              )}
              
              {newTicket.category === 'telefon' && (
                <>
                  <div>
                    <Label htmlFor="contract_number">İstifadəçi</Label>
                    <Input
                      id="contract_number"
                      value={newTicket.contract_number}
                      onChange={(e) => setNewTicket({ ...newTicket, contract_number: e.target.value })}
                      data-testid="contract-number-input"
                      className="mt-1.5 border-gray-300"
                    />
                  </div>
                  <div>
                    <Label htmlFor="service_password">Şifrə</Label>
                    <Input
                      id="service_password"
                      type="text"
                      value={newTicket.service_password}
                      onChange={(e) => setNewTicket({ ...newTicket, service_password: e.target.value })}
                      data-testid="service-password-input"
                      className="mt-1.5 border-gray-300"
                    />
                  </div>
                  <div>
                    <Label htmlFor="city_phone">Şəhər telefonu</Label>
                    <Input
                      id="city_phone"
                      value={newTicket.city_phone || ''}
                      onChange={(e) => setNewTicket({ ...newTicket, city_phone: e.target.value })}
                      data-testid="city-phone-input"
                      className="mt-1.5 border-gray-300"
                      placeholder="012-xxx-xx-xx"
                    />
                  </div>
                  <div>
                    <Label htmlFor="city_phone_password">Şəhər telefonunun parolu</Label>
                    <Input
                      id="city_phone_password"
                      type="text"
                      value={newTicket.city_phone_password || ''}
                      onChange={(e) => setNewTicket({ ...newTicket, city_phone_password: e.target.value })}
                      data-testid="city-phone-password-input"
                      className="mt-1.5 border-gray-300"
                    />
                  </div>
                </>
              )}
              
              <div>
                <Label htmlFor="subscriber_location">Ünvan</Label>
                <Input
                  id="subscriber_location"
                  value={newTicket.subscriber_location}
                  onChange={(e) => setNewTicket({ ...newTicket, subscriber_location: e.target.value })}
                  required
                  data-testid="subscriber-location-input"
                  className="mt-1.5 border-gray-300"
                />
              </div>
              
              {/* Google Maps URL or Coordinates */}
              <div>
                <Label htmlFor="maps_url">Google Maps URL və ya Koordinatlar</Label>
                <Input
                  id="maps_url"
                  type="text"
                  placeholder="https://maps.google.com/?q=40.4093,49.8671 və ya 40.4093, 49.8671"
                  onChange={(e) => {
                    const value = e.target.value.trim();
                    // Try to parse Google Maps URL
                    let lat = null, lng = null;
                    
                    // Pattern 1: https://maps.google.com/?q=40.4093,49.8671
                    const qMatch = value.match(/[?&]q=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
                    if (qMatch) {
                      lat = parseFloat(qMatch[1]);
                      lng = parseFloat(qMatch[2]);
                    }
                    
                    // Pattern 2: https://www.google.com/maps/@40.4093,49.8671,15z
                    const atMatch = value.match(/@(-?\d+\.?\d*),(-?\d+\.?\d*)/);
                    if (!lat && atMatch) {
                      lat = parseFloat(atMatch[1]);
                      lng = parseFloat(atMatch[2]);
                    }
                    
                    // Pattern 3: https://goo.gl/maps/... or direct coordinates "40.4093, 49.8671"
                    const coordMatch = value.match(/(-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)/);
                    if (!lat && coordMatch) {
                      lat = parseFloat(coordMatch[1]);
                      lng = parseFloat(coordMatch[2]);
                    }
                    
                    // Pattern 4: https://maps.google.com/maps?ll=40.4093,49.8671
                    const llMatch = value.match(/ll=(-?\d+\.?\d*),(-?\d+\.?\d*)/);
                    if (!lat && llMatch) {
                      lat = parseFloat(llMatch[1]);
                      lng = parseFloat(llMatch[2]);
                    }
                    
                    if (lat && lng && !isNaN(lat) && !isNaN(lng)) {
                      setNewTicket({ ...newTicket, latitude: lat, longitude: lng });
                    }
                  }}
                  data-testid="maps-url-input"
                  className="mt-1.5 border-gray-300"
                />
                <p className="text-xs text-zinc-500 mt-1">Google Maps linkini yapışdırın, koordinatlar avtomatik çıxarılacaq</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="latitude">Enlik (Latitude)</Label>
                  <Input
                    id="latitude"
                    type="number"
                    step="any"
                    value={newTicket.latitude || ''}
                    onChange={(e) => setNewTicket({ ...newTicket, latitude: parseFloat(e.target.value) })}
                    data-testid="latitude-input"
                    className="mt-1.5 border-gray-300"
                  />
                </div>
                <div>
                  <Label htmlFor="longitude">Uzunluq (Longitude)</Label>
                  <Input
                    id="longitude"
                    type="number"
                    step="any"
                    value={newTicket.longitude || ''}
                    onChange={(e) => setNewTicket({ ...newTicket, longitude: parseFloat(e.target.value) })}
                    data-testid="longitude-input"
                    className="mt-1.5 border-gray-300"
                  />
                </div>
              </div>
              
              {/* Dynamic Problem Types */}
              {problemTypes[newTicket.category] && problemTypes[newTicket.category].length > 0 && (
                <div>
                  <Label>Problem növü</Label>
                  <div className="space-y-2 mt-2">
                    {problemTypes[newTicket.category].map(pt => (
                      <div key={pt.id} className="flex items-center">
                        <input
                          type="radio"
                          id={pt.code}
                          name="problem_type"
                          value={pt.code}
                          checked={newTicket.problem_type === pt.code}
                          onChange={(e) => setNewTicket({ 
                            ...newTicket, 
                            problem_type: e.target.value, 
                            problem_description: pt.code === 'other' ? '' : pt.description 
                          })}
                          className="mr-2"
                        />
                        <label htmlFor={pt.code}>{pt.name}</label>
                      </div>
                    ))}
                    {newTicket.problem_type === 'other' && (
                      <Textarea
                        placeholder="Problemi təsvir edin"
                        value={newTicket.problem_description}
                        onChange={(e) => setNewTicket({ ...newTicket, problem_description: e.target.value })}
                        className="mt-2 border-gray-300"
                        required
                      />
                    )}
                  </div>
                </div>
              )}
              
              {/* Fallback if no problem types exist for category */}
              {(!problemTypes[newTicket.category] || problemTypes[newTicket.category].length === 0) && (
                <div>
                  <Label htmlFor="problem_description">Problem təsviri</Label>
                  <Textarea
                    id="problem_description"
                    value={newTicket.problem_description}
                    onChange={(e) => setNewTicket({ ...newTicket, problem_description: e.target.value })}
                    required
                    data-testid="problem-description-input"
                    className="mt-1.5 border-gray-300"
                  />
                </div>
              )}
              <div>
                <Label htmlFor="operator_notes">Operator qeydləri</Label>
                <Textarea
                  id="operator_notes"
                  value={newTicket.operator_notes}
                  onChange={(e) => setNewTicket({ ...newTicket, operator_notes: e.target.value })}
                  data-testid="operator-notes-input"
                  className="mt-1.5 border-gray-300"
                />
              </div>
              
              {/* Auto-assign toggle */}
              <div className="flex items-center justify-between p-4 bg-zinc-50 rounded-lg border border-zinc-200">
                <div>
                  <Label className="text-base font-medium">Avtomatik texnik təyinatı</Label>
                  <p className="text-sm text-zinc-500 mt-1">
                    Aktivdirsə, ən uyğun texnik avtomatik təyin ediləcək (GPS və ya iş yükünə görə)
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setNewTicket({ ...newTicket, auto_assign: !newTicket.auto_assign })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    newTicket.auto_assign ? 'bg-green-500' : 'bg-zinc-300'
                  }`}
                  data-testid="auto-assign-toggle"
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      newTicket.auto_assign ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              
              <Button type="submit" className="w-full bg-primary hover:bg-primary/90 text-white" data-testid="submit-ticket-button">
                {newTicket.auto_assign ? 'Yarat və Avtomatik Təyin Et' : 'Ticket Yarat'}
              </Button>
            </form>
          </DialogContent>
        </Dialog>

        {/* Tickets by Tab */}
        <div className="space-y-4">
          {/* Tab Description */}
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-bold text-zinc-900">
              {activeTab === 'pending' && 'Gözləyən Ticketlər'}
              {activeTab === 'assigned' && 'Təyin olunmuş Ticketlər'}
              {activeTab === 'completed' && 'Tamamlanmış Ticketlər (Zəng üçün)'}
              {activeTab === 'closed' && 'Bağlanmış Ticketlər'}
              {activeTab === 'all' && 'Bütün Ticketlər'}
            </h2>
            <span className="text-sm text-zinc-500">
              {currentTabTickets.length} ticket
              {!showClosedTickets && activeTab === 'closed' && ' (gizli)'}
            </span>
          </div>

          {/* Tab Description Text */}
          <p className="text-sm text-zinc-500 bg-zinc-100 p-3 rounded-lg">
            {activeTab === 'pending' && '🕐 Bu ticketlər hələ texnikə təyin olunmayıb. Texnik seçib təyin edin.'}
            {activeTab === 'assigned' && '👷 Bu ticketlər texniklərə təyin olunub və gözlənilir. Texnikin işləməsini gözləyin.'}
            {activeTab === 'completed' && '✅ Texnik işi tamamlayıb. Müştəriyə zəng edib işi bağlayın və ya geri qaytarın.'}
            {activeTab === 'closed' && '🏁 Bu ticketlər bağlanıb. Arxiv məlumatları.'}
            {activeTab === 'all' && '📋 Bütün ticketlərin siyahısı (filtrlərə görə).'}
          </p>

          {/* Tickets List */}
          {currentTabTickets.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg border border-zinc-200">
              <p className="text-zinc-500">
                {activeTab === 'closed' && !showClosedTickets 
                  ? 'Bağlanmış ticketlər gizlidir. Görmək üçün yuxarıdakı "Bağlanmış görünür" düyməsini basın.'
                  : 'Bu tab-da ticket yoxdur'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {currentTabTickets.map(ticket => (
                <TicketCard key={ticket.id} ticket={ticket} onCardClick={openDetailDialog}>
                  {/* Pending ticket - show technician assignment */}
                  {ticket.status === 'pending' && (
                    <>
                      {/* Show previous technician info if ticket was returned */}
                      {ticket.previous_technician_id && (
                        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                          <p className="text-sm text-red-700 font-semibold">
                            ⚠️ Bu ticket geri qaytarılmışdır! ({ticket.previous_technician_full_name || ticket.previous_technician_name})
                          </p>
                          {ticket.rejection_reason && (
                            <p className="text-xs text-red-600 mt-1">Səbəb: {ticket.rejection_reason}</p>
                          )}
                        </div>
                      )}
                      
                      <div className="flex gap-2 mt-4">
                        <Button 
                          className="flex-1 bg-primary text-white" 
                          onClick={() => openAssignDialog(ticket)}
                          data-testid={`assign-tech-btn-${ticket.id}`}
                        >
                          Texnik Təyin Et
                        </Button>
                      </div>
                    </>
                  )}

                  {/* Comment/Image preview for assigned/completed/closed */}
                  {['assigned', 'in_progress', 'completed', 'closed'].includes(ticket.status) && (ticket.comments?.length > 0 || ticket.images?.length > 0) && (
                    <div 
                      onClick={async (e) => {
                        e.stopPropagation(); // Prevent card click from opening service details
                        // Show dialog with basic info first
                        setSelectedTicketForDetails(ticket);
                        setShowCommentImageDialog(true);
                        
                        // Then fetch full details including images
                        try {
                          const token = localStorage.getItem('token');
                          const response = await axios.get(`${API}/tickets/${ticket.id}`, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          setSelectedTicketForDetails(response.data);
                        } catch (error) {
                          console.error('Error fetching ticket details:', error);
                        }
                      }}
                      className="mt-3 p-3 bg-zinc-50 rounded-lg cursor-pointer hover:bg-zinc-100 transition-colors border border-zinc-200"
                      data-testid={`ticket-details-preview-${ticket.id}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3 text-sm text-zinc-600">
                          {ticket.comments?.length > 0 && (
                            <span className="flex items-center gap-1">
                              <MessageSquare className="h-4 w-4" />
                              {ticket.comments.length} şərh
                            </span>
                          )}
                          {ticket.images?.length > 0 && (
                            <span className="flex items-center gap-1">
                              <ImageIcon className="h-4 w-4" />
                              {ticket.images.length} şəkil
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-blue-600">Hamısına bax →</span>
                      </div>
                      {ticket.comments?.length > 0 && (
                        <div className="text-sm">
                          <span className="font-medium text-zinc-700">
                            {ticket.comments[ticket.comments.length - 1]?.full_name || ticket.comments[ticket.comments.length - 1]?.username}:
                          </span>
                          <span className="text-zinc-600 ml-1">
                            {ticket.comments[ticket.comments.length - 1]?.text?.slice(0, 80)}
                            {ticket.comments[ticket.comments.length - 1]?.text?.length > 80 ? '...' : ''}
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Completed ticket - show close/reject buttons */}
                  {ticket.status === 'completed' && (
                    <>
                      <div className="flex gap-2 mt-4">
                        <Button
                          onClick={() => openCloseDialog(ticket)}
                          className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                          data-testid={`close-ticket-${ticket.id}`}
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Bağla
                        </Button>
                        <Button
                          onClick={() => handleShowRejectDialog(ticket)}
                          variant="destructive"
                          className="flex-1"
                          data-testid={`reject-ticket-${ticket.id}`}
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Geri qaytart
                        </Button>
                      </div>
                      <a
                        href={`tel:${ticket.subscriber_phone}`}
                        className="flex items-center justify-center gap-2 mt-2 p-2 bg-blue-50 hover:bg-blue-100 text-blue-700 rounded-lg transition-colors"
                        data-testid={`call-subscriber-${ticket.id}`}
                      >
                        <Phone className="h-4 w-4" />
                        {ticket.subscriber_phone}
                      </a>
                    </>
                  )}
                </TicketCard>
              ))}
            </div>
          )}
        </div>
      </div>
      </>
      )}

      {/* Rejection Reason Dialog */}
      {showRejectDialog && rejectTicket && (
      <Dialog open={showRejectDialog} onOpenChange={safeCloseRejectDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-zinc-900">
              Ticketi geri qaytar
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Warning */}
            <div className="p-4 rounded-lg border bg-red-50 border-red-200">
              <div className="flex items-start gap-3">
                <AlertTriangle className="h-5 w-5 mt-0.5 text-red-600" />
                <div>
                  <p className="font-semibold text-red-700">
                    Texnikdən -20 xal silinəcək
                  </p>
                  <p className="text-sm text-zinc-600 mt-1">
                    Texnik: {rejectTicket?.assigned_technician_name}
                  </p>
                </div>
              </div>
            </div>
            
            {/* Rejection Reason */}
            <div>
              <Label htmlFor="rejection_reason">Geri qaytarma səbəbi *</Label>
              <Textarea
                id="rejection_reason"
                value={rejectionReason}
                onChange={(e) => setRejectionReason(e.target.value)}
                placeholder="Məsələn: İş yarımçıq qalıb, Şəkil keyfiyyəti aşağıdır, Müştəri şikayət edib..."
                className="mt-1.5"
                rows={3}
                data-testid="rejection-reason-input"
              />
            </div>
            
            {/* Quick reasons */}
            <div className="flex flex-wrap gap-2">
              {['İş yarımçıq qalıb', 'Şəkil göndərilməyib', 'Müştəri şikayət edib', 'Texniki problem həll olunmayıb'].map((reason) => (
                <Button
                  key={reason}
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setRejectionReason(reason)}
                  className="text-xs"
                >
                  {reason}
                </Button>
              ))}
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowRejectDialog(false)}
              data-testid="cancel-reject-button"
            >
              Ləğv et
            </Button>
            <Button
              onClick={handleRejectTicket}
              disabled={!rejectionReason.trim()}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="confirm-reject-button"
            >
              <XCircle className="h-4 w-4 mr-2" />
              Geri qaytar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}

      {/* Assign Technician Dialog */}
      {showAssignDialog && assignTicket && (
      <Dialog open={showAssignDialog} onOpenChange={safeCloseAssignDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-zinc-900">
              Texnik Təyin Et
            </DialogTitle>
            <DialogDescription>
              {assignTicket.subscriber_name} - {assignTicket.subscriber_phone}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Technician Selection */}
            <div>
              <Label>Texnik *</Label>
              <Select 
                value={assignData.technician_id} 
                onValueChange={(value) => setAssignData({...assignData, technician_id: value})}
              >
                <SelectTrigger className="mt-1.5" data-testid="assign-dialog-tech-select">
                  <SelectValue placeholder="Texnik seçin" />
                </SelectTrigger>
                <SelectContent>
                  {assignTicket && getTechniciansForTicket(assignTicket).map(tech => {
                    const isDisabled = assignTicket.previous_technician_id === tech.id;
                    return (
                      <SelectItem 
                        key={tech.id} 
                        value={tech.id} 
                        disabled={isDisabled}
                        className={isDisabled ? 'text-red-500 line-through bg-red-50' : 'text-zinc-900'}
                      >
                        {tech.full_name || tech.username}
                        {isDisabled && ' (geri qaytarılıb)'}
                      </SelectItem>
                    );
                  })}
                  {assignTicket && getTechniciansForTicket(assignTicket).length === 0 && (
                    <SelectItem value="no-tech" disabled className="text-gray-500">
                      Bu qrupda texnik yoxdur
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>
            
            {/* Scheduled Date */}
            <div>
              <Label>Planlanmış Tarix</Label>
              <Input
                type="date"
                value={assignData.scheduled_date}
                onChange={(e) => setAssignData({...assignData, scheduled_date: e.target.value})}
                className="mt-1.5"
                min={new Date().toISOString().split('T')[0]}
                data-testid="assign-dialog-date"
              />
            </div>
            
            {/* Scheduled Time */}
            <div>
              <Label>Planlanmış Vaxt</Label>
              <Input
                type="time"
                value={assignData.scheduled_time}
                onChange={(e) => setAssignData({...assignData, scheduled_time: e.target.value})}
                className="mt-1.5"
                data-testid="assign-dialog-time"
              />
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowAssignDialog(false)}
              data-testid="cancel-assign-button"
            >
              Ləğv et
            </Button>
            <Button
              onClick={() => handleAssignTechnician(
                assignTicket?.id, 
                assignData.technician_id,
                assignData.scheduled_date,
                assignData.scheduled_time
              )}
              disabled={!assignData.technician_id}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="confirm-assign-button"
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Təyin Et
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}

      {/* Close Ticket Dialog with Customer Satisfaction */}
      {showCloseDialog && closeTicket && (
        <Dialog open={showCloseDialog} onOpenChange={safeCloseCloseDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-zinc-900">
                Ticketi Bağla
              </DialogTitle>
              <DialogDescription>
                {closeTicket.subscriber_name} - {closeTicket.subscriber_phone}
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <Label className="text-base font-medium">Abunəçinin texniki qiymətləndirməsi</Label>
              <div className="flex gap-3">
                <Button
                  type="button"
                  onClick={() => setCustomerSatisfaction('satisfied')}
                  className={`flex-1 h-20 flex-col gap-2 ${
                    customerSatisfaction === 'satisfied' 
                      ? 'bg-green-600 hover:bg-green-700 text-white border-2 border-green-700' 
                      : 'bg-green-50 hover:bg-green-100 text-green-700 border-2 border-green-200'
                  }`}
                  data-testid="satisfaction-happy"
                >
                  <span className="text-2xl">😊</span>
                  <span className="text-sm font-medium">Razı qaldı</span>
                </Button>
                <Button
                  type="button"
                  onClick={() => setCustomerSatisfaction('unsatisfied')}
                  className={`flex-1 h-20 flex-col gap-2 ${
                    customerSatisfaction === 'unsatisfied' 
                      ? 'bg-red-600 hover:bg-red-700 text-white border-2 border-red-700' 
                      : 'bg-red-50 hover:bg-red-100 text-red-700 border-2 border-red-200'
                  }`}
                  data-testid="satisfaction-unhappy"
                >
                  <span className="text-2xl">😞</span>
                  <span className="text-sm font-medium">Narazı qaldı</span>
                </Button>
              </div>
            </div>
            
            <DialogFooter className="gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCloseDialog(false);
                  setCloseTicket(null);
                  setCustomerSatisfaction(null);
                }}
                data-testid="cancel-close-button"
              >
                Ləğv et
              </Button>
              <Button
                onClick={() => handleCloseTicket(closeTicket.id, customerSatisfaction)}
                disabled={!customerSatisfaction}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="confirm-close-button"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Bağla
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Comment and Image Details Dialog */}
      {showCommentImageDialog && selectedTicketForDetails && (
      <Dialog open={showCommentImageDialog} onOpenChange={safeCloseCommentDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5 text-blue-600" />
              Şərhlər və Şəkillər - #{selectedTicketForDetails.id?.slice(0, 8)}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6">
            {/* Ticket Info */}
            <div className="bg-zinc-50 p-3 rounded-lg">
              <p className="font-semibold text-zinc-900">{selectedTicketForDetails.subscriber_name}</p>
              <p className="text-sm text-zinc-600">{selectedTicketForDetails.subscriber_phone}</p>
              <p className="text-sm text-zinc-600">{selectedTicketForDetails.subscriber_location}</p>
            </div>

            {/* Images Section */}
            {selectedTicketForDetails.images?.length > 0 && (
              <div>
                <h3 className="font-semibold text-zinc-900 mb-3 flex items-center gap-2">
                  <ImageIcon className="h-5 w-5" />
                  Şəkillər ({selectedTicketForDetails.images.length})
                  <span className="text-xs font-normal text-zinc-500 ml-2">— kliklə böyüt</span>
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {selectedTicketForDetails.images.map((img, idx) => (
                    <button 
                      key={idx} 
                      onClick={async () => {
                        // Show thumbnails first
                        setImageViewerImages(selectedTicketForDetails.images);
                        setImageViewerIndex(idx);
                        setShowImageViewer(true);
                        
                        // Then fetch full images
                        try {
                          const token = localStorage.getItem('token');
                          const response = await axios.get(`${API}/tickets/${selectedTicketForDetails.id}/full-images`, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          if (response.data.images && response.data.images.length > 0) {
                            setImageViewerImages(response.data.images);
                          }
                        } catch (error) {
                          console.error('Error fetching full images:', error);
                        }
                      }}
                      className="block aspect-square rounded-lg overflow-hidden border border-zinc-200 hover:border-blue-400 hover:ring-2 hover:ring-blue-200 transition-all cursor-zoom-in"
                    >
                      <img 
                        src={getImageUrl(img)} 
                        alt={`Şəkil ${idx + 1}`} 
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Comments Section */}
            {selectedTicketForDetails.comments?.length > 0 && (
              <div>
                <h3 className="font-semibold text-zinc-900 mb-3 flex items-center gap-2">
                  <MessageSquare className="h-5 w-5" />
                  Şərhlər ({selectedTicketForDetails.comments.length})
                </h3>
                <div className="space-y-3">
                  {selectedTicketForDetails.comments.map((comment) => (
                    <div key={comment.id} className="bg-zinc-50 p-3 rounded-lg">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-sm text-zinc-900">
                          {comment.full_name || comment.username}
                          <span className="ml-2 text-xs font-normal text-zinc-500">
                            ({comment.role === 'technician' ? 'Texnik' : 'Operator'})
                          </span>
                        </span>
                        <span className="text-xs text-zinc-500">
                          {new Date(comment.created_at).toLocaleString('az-AZ')}
                        </span>
                      </div>
                      <p className="text-sm text-zinc-700">{comment.text}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* No content message */}
            {!selectedTicketForDetails.images?.length && !selectedTicketForDetails.comments?.length && (
              <p className="text-center text-zinc-500 py-4">Heç bir şərh və ya şəkil yoxdur</p>
            )}
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => safeCloseCommentDialog(false)}
            >
              Bağla
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      )}

      {/* Detailed View and Reschedule Dialog */}
      {showDetailDialog && detailTicket && (
        <Dialog open={showDetailDialog} onOpenChange={() => {}}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" onPointerDownOutside={(e) => e.preventDefault()} onInteractOutside={(e) => e.preventDefault()}>
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-primary" />
                  <span>Servis Detalları - #{detailTicket.id?.slice(0, 8)}</span>
                  <Button variant="ghost" size="sm" className="ml-auto" onClick={() => safeCloseDetailDialog(false)}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className={`p-2 rounded-full ${canReschedule ? 'bg-green-100 text-green-600' : 'bg-zinc-100 text-zinc-400'}`}>
                        {canReschedule ? <Edit2 className="h-4 w-4" /> : <Lock className="h-4 w-4" />}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      {canReschedule ? 'Redaktə edilə bilər' : (rescheduleReason || 'Redaktə edilə bilməz')}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </DialogTitle>
              <DialogDescription>
                Status: <span className="font-semibold">{
                  {pending: 'Gözləyir', assigned: 'Təyin olunub', in_progress: 'İcrada', completed: 'Tamamlanıb', closed: 'Bağlanıb', rejected: 'Geri qaytarılıb', declined: 'Rədd edilib'}[detailTicket.status] || detailTicket.status
                }</span>
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-6 py-4">
              {/* Abunəçi Məlumatları */}
              <div className="bg-zinc-50 p-4 rounded-lg space-y-3">
                <h4 className="font-semibold text-zinc-900 flex items-center gap-2">
                  <User className="h-4 w-4" /> Abunəçi Məlumatları
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-zinc-500">Ad Soyad</p>
                    <p className="font-medium">{detailTicket.subscriber_name}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500">Telefon 1</p>
                    <p className="font-medium">{detailTicket.subscriber_phone}</p>
                  </div>
                  {detailTicket.subscriber_phone_2 && (
                    <div>
                      <p className="text-zinc-500">Telefon 2</p>
                      <p className="font-medium">{detailTicket.subscriber_phone_2}</p>
                    </div>
                  )}
                  <div className="md:col-span-2">
                    <p className="text-zinc-500">Ünvan</p>
                    <p className="font-medium">{detailTicket.subscriber_location}</p>
                  </div>
                  {(detailTicket.latitude && detailTicket.longitude) && (
                    <div className="md:col-span-2">
                      <p className="text-zinc-500">GPS Koordinatları</p>
                      <div className="flex items-center gap-2">
                        <p className="font-medium font-mono text-sm">
                          {detailTicket.latitude?.toFixed(6)}, {detailTicket.longitude?.toFixed(6)}
                        </p>
                        <a 
                          href={`https://www.google.com/maps?q=${detailTicket.latitude},${detailTicket.longitude}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 text-sm underline"
                        >
                          Xəritədə göstər
                        </a>
                      </div>
                    </div>
                  )}
                  {detailTicket.contract_number && (
                    <div>
                      <p className="text-zinc-500">Müqavilə nömrəsi</p>
                      <p className="font-medium">{detailTicket.contract_number}</p>
                    </div>
                  )}
                  {detailTicket.service_password && (
                    <div>
                      <p className="text-zinc-500">Xidmət şifrəsi</p>
                      <p className="font-medium">{detailTicket.service_password}</p>
                    </div>
                  )}
                  {detailTicket.city_phone && (
                    <div>
                      <p className="text-zinc-500">Şəhər telefonu</p>
                      <p className="font-medium">{detailTicket.city_phone}</p>
                    </div>
                  )}
                  {detailTicket.city_phone_password && (
                    <div>
                      <p className="text-zinc-500">Şəhər tel. şifrəsi</p>
                      <p className="font-medium">{detailTicket.city_phone_password}</p>
                    </div>
                  )}
                  {(detailTicket.onu_rx_power || detailTicket.onu_rx_power === 0) && (
                    <div>
                      <p className="text-zinc-500">ONU RX Power</p>
                      <p className={`font-medium ${parseFloat(detailTicket.onu_rx_power) < -28 ? 'text-red-600' : 'text-green-600'}`}>
                        {detailTicket.onu_rx_power} dBm
                      </p>
                    </div>
                  )}
                  {(detailTicket.onu_tx_power || detailTicket.onu_tx_power === 0) && (
                    <div>
                      <p className="text-zinc-500">ONU TX Power</p>
                      <p className="font-medium">{detailTicket.onu_tx_power} dBm</p>
                    </div>
                  )}
                  {(detailTicket.olt_rx_power || detailTicket.olt_rx_power === 0) && (
                    <div>
                      <p className="text-zinc-500">OLT RX Power</p>
                      <p className={`font-medium ${parseFloat(detailTicket.olt_rx_power) < -28 ? 'text-red-600' : 'text-green-600'}`}>
                        {detailTicket.olt_rx_power} dBm
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Servis Məlumatları */}
              <div className="bg-blue-50 p-4 rounded-lg space-y-3">
                <h4 className="font-semibold text-blue-900 flex items-center gap-2">
                  <Wifi className="h-4 w-4" /> Servis Məlumatları
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-blue-700">Kateqoriya</p>
                    <p className="font-medium text-blue-900">
                      {{internet: 'İnternet', iptv: 'IPTV', catv: 'CaTV', telefon: 'Telefon'}[detailTicket.category] || detailTicket.category}
                    </p>
                  </div>
                  <div>
                    <p className="text-blue-700">Qrup/Region</p>
                    <p className="font-medium text-blue-900">{detailTicket.group_name || 'Təyin edilməyib'}</p>
                  </div>
                  {detailTicket.problem_type && (
                    <div>
                      <p className="text-blue-700">Problem növü</p>
                      <p className="font-medium text-blue-900">{detailTicket.problem_type}</p>
                    </div>
                  )}
                  <div className="md:col-span-2">
                    <p className="text-blue-700">Problem təsviri</p>
                    <p className="font-medium text-blue-900">{detailTicket.problem_description}</p>
                  </div>
                  {detailTicket.operator_notes && (
                    <div className="md:col-span-2">
                      <p className="text-blue-700">Operator qeydləri</p>
                      <p className="font-medium text-blue-900 whitespace-pre-wrap">{detailTicket.operator_notes}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Təyinat Məlumatları */}
              {(detailTicket.assigned_technician_name || detailTicket.scheduled_date) && (
                <div className="bg-purple-50 p-4 rounded-lg space-y-3">
                  <h4 className="font-semibold text-purple-900 flex items-center gap-2">
                    <Calendar className="h-4 w-4" /> Təyinat Məlumatları
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    {detailTicket.assigned_technician_name && (
                      <div>
                        <p className="text-purple-700">Texnik</p>
                        <p className="font-medium text-purple-900">{detailTicket.assigned_technician_name}</p>
                      </div>
                    )}
                    {detailTicket.scheduled_date && (
                      <div>
                        <p className="text-purple-700">Planlanmış tarix</p>
                        <p className="font-medium text-purple-900">{detailTicket.scheduled_date}</p>
                      </div>
                    )}
                    {detailTicket.scheduled_time && (
                      <div>
                        <p className="text-purple-700">Planlanmış vaxt</p>
                        <p className="font-medium text-purple-900">{detailTicket.scheduled_time}</p>
                      </div>
                    )}
                    {detailTicket.assigned_at && (
                      <div>
                        <p className="text-purple-700">Təyin edilmə vaxtı</p>
                        <p className="font-medium text-purple-900">{new Date(detailTicket.assigned_at).toLocaleString('az-AZ')}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Əvvəlki Təyinat (varsa) */}
              {detailTicket.previous_technician_name && (
                <div className="bg-red-50 p-4 rounded-lg space-y-3">
                  <h4 className="font-semibold text-red-900 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" /> Əvvəlki Təyinat
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                    <div>
                      <p className="text-red-700">Əvvəlki texnik</p>
                      <p className="font-medium text-red-900">{detailTicket.previous_technician_full_name || detailTicket.previous_technician_name}</p>
                    </div>
                    {detailTicket.rejection_reason && (
                      <div>
                        <p className="text-red-700">İmtina səbəbi</p>
                        <p className="font-medium text-red-900">{detailTicket.rejection_reason}</p>
                      </div>
                    )}
                    {detailTicket.decline_reason && (
                      <div>
                        <p className="text-red-700">Rədd səbəbi</p>
                        <p className="font-medium text-red-900">{detailTicket.decline_reason}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Tarixçə */}
              <div className="bg-zinc-100 p-4 rounded-lg space-y-3">
                <h4 className="font-semibold text-zinc-900 flex items-center gap-2">
                  <Clock className="h-4 w-4" /> Tarixçə
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-zinc-600">Yaradılma tarixi</p>
                    <p className="font-medium">{new Date(detailTicket.created_at).toLocaleString('az-AZ')}</p>
                  </div>
                  {detailTicket.created_by_name && (
                    <div>
                      <p className="text-zinc-600">Yaradan</p>
                      <p className="font-medium">{detailTicket.created_by_name}</p>
                    </div>
                  )}
                  {detailTicket.completed_at && (
                    <div>
                      <p className="text-zinc-600">Tamamlanma tarixi</p>
                      <p className="font-medium">{new Date(detailTicket.completed_at).toLocaleString('az-AZ')}</p>
                    </div>
                  )}
                  {detailTicket.closed_at && (
                    <div>
                      <p className="text-zinc-600">Bağlanma tarixi</p>
                      <p className="font-medium">{new Date(detailTicket.closed_at).toLocaleString('az-AZ')}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Texnik Şəkilləri */}
              {detailTicket.images?.length > 0 && (
                <div className="bg-green-50 p-4 rounded-lg space-y-3">
                  <h4 className="font-semibold text-green-900 flex items-center gap-2">
                    <ImageIcon className="h-4 w-4" /> Texnikin Şəkilləri ({detailTicket.images.length})
                  </h4>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {detailTicket.images.map((img, idx) => (
                      <button 
                        key={idx} 
                        onClick={() => openImageInNewTab(img, detailTicket.id, idx)}
                        className="aspect-square rounded-lg overflow-hidden border border-green-200 hover:border-green-400 hover:ring-2 hover:ring-green-200 transition-all cursor-zoom-in"
                      >
                        <img 
                          src={getImageUrl(img)} 
                          alt={`Şəkil ${idx + 1}`} 
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-green-600">Böyütmək üçün şəklin üzərinə klikləyin (yeni tab-da açılacaq)</p>
                </div>
              )}

              {/* Şərhlər */}
              {detailTicket.comments?.length > 0 && (
                <div className="bg-indigo-50 p-4 rounded-lg space-y-3">
                  <h4 className="font-semibold text-indigo-900 flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" /> Şərhlər ({detailTicket.comments.length})
                  </h4>
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {detailTicket.comments.map((comment) => (
                      <div key={comment.id} className="bg-white p-3 rounded-lg border border-indigo-100">
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-semibold text-sm text-indigo-900">
                            {comment.full_name || comment.username}
                            {comment.role === 'technician' && <span className="ml-1 text-indigo-600">(Texnik)</span>}
                          </span>
                          <span className="text-xs text-indigo-500">
                            {new Date(comment.created_at).toLocaleString('az-AZ')}
                          </span>
                        </div>
                        <p className="text-sm text-indigo-800">{comment.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Redaktə Forması */}
              {canReschedule && (
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold text-zinc-900 flex items-center gap-2">
                      <Edit2 className="h-4 w-4" /> Yenidən Planlaşdır
                    </h4>
                    <Button 
                      variant={isEditMode ? "default" : "outline"}
                      size="sm"
                      onClick={() => setIsEditMode(!isEditMode)}
                    >
                      {isEditMode ? 'Ləğv et' : 'Redaktə et'}
                    </Button>
                  </div>
                  
                  {isEditMode && (
                    <div className="space-y-4 bg-yellow-50 p-4 rounded-lg">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label>Texnik</Label>
                          <Select 
                            value={rescheduleData.technician_id} 
                            onValueChange={(value) => setRescheduleData({...rescheduleData, technician_id: value})}
                          >
                            <SelectTrigger>
                              <SelectValue placeholder="Texnik seçin" />
                            </SelectTrigger>
                            <SelectContent>
                              {groupTechnicians.map(tech => (
                                <SelectItem key={tech.id} value={tech.id}>
                                  {tech.full_name || tech.username}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <Label>Tarix</Label>
                            <Input 
                              type="date"
                              value={rescheduleData.scheduled_date}
                              min={new Date().toISOString().split('T')[0]}
                              onChange={(e) => setRescheduleData({...rescheduleData, scheduled_date: e.target.value})}
                            />
                          </div>
                          <div>
                            <Label>Vaxt</Label>
                            <Input 
                              type="time"
                              value={rescheduleData.scheduled_time}
                              onChange={(e) => setRescheduleData({...rescheduleData, scheduled_time: e.target.value})}
                            />
                          </div>
                        </div>
                      </div>
                      <div>
                        <Label>Qeyd (istəyə bağlı)</Label>
                        <Textarea 
                          value={rescheduleData.operator_notes}
                          onChange={(e) => setRescheduleData({...rescheduleData, operator_notes: e.target.value})}
                          placeholder="Dəyişiklik səbəbini qeyd edin..."
                          className="mt-1"
                        />
                      </div>
                      <Button onClick={handleReschedule} className="w-full bg-primary">
                        Dəyişiklikləri Yadda Saxla
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* Redaktə edilə bilməmə səbəbi */}
              {!canReschedule && rescheduleReason && (
                <div className="bg-zinc-100 p-4 rounded-lg flex items-center gap-3">
                  <Lock className="h-5 w-5 text-zinc-500" />
                  <p className="text-sm text-zinc-600">
                    <span className="font-medium">Redaktə edilə bilməz:</span> {rescheduleReason}
                  </p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => safeCloseDetailDialog(false)}>
                Bağla
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Full Screen Image Viewer */}
      {showImageViewer && imageViewerImages.length > 0 && (
        <ImageViewer
          images={imageViewerImages}
          initialIndex={imageViewerIndex}
          onClose={() => {
            setShowImageViewer(false);
            setImageViewerImages([]);
            setImageViewerIndex(0);
          }}
        />
      )}
    </div>
  );
}
